import React from "react";
import { useHistory } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as yup from "yup";
import { useMutation } from "react-query";
import { Button, Input } from "antd";
import styled from "styled-components";
import UIOutlet from "components/common/UIOutlet";
import { resetPasswordRequest } from "services/apiSrv";
import { show_toast } from "utils/helpers";

// Styled components
const FormContainer = styled.div`
    max-width: 400px;
    margin: 0 auto;
    padding: 24px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: calc(100vh - 200px);
`;

const PageHeader = styled.div`
    margin-bottom: 24px;
    text-align: center;
`;

const Title = styled.h1`
    font-size: 20px;
    font-weight: 600;
    color: #001F5C;
    margin: 0 0 6px 0;
    letter-spacing: -0.02em;
`;

const Divider = styled.div`
    width: 40px;
    height: 3px;
    background: #EF3340;
    border-radius: 2px;
    margin: 0 auto;
`;

const StyledFormItem = styled.div`
    margin-bottom: 16px;
    width: 100%;

    .form-label {
        font-size: 12px;
        font-weight: 500;
        color: #595959;
        height: 20px;
        display: block;
        margin-bottom: 4px;
    }

    .ant-input {
        border-radius: 6px;
        border-color: #e8e8e8;

        &:focus,
        &:hover {
            border-color: #00B8D4;
        }
    }

    .error-message {
        color: #ff4d4f;
        font-size: 12px;
        margin-top: 4px;
    }
`;

const ActionButtons = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
    width: 100%;
`;

const SubmitButton = styled(Button)`
    background: #001F5C;
    border-color: #001F5C;
    height: 36px;
    padding: 0 24px;
    font-weight: 500;
    font-size: 14px;

    &:hover,
    &:focus {
        background: #00306B;
        border-color: #00306B;
    }
`;

const CancelButton = styled(Button)`
    height: 36px;
    padding: 0 24px;
    font-weight: 500;
    font-size: 14px;
    border-color: #d9d9d9;
    color: #595959;

    &:hover,
    &:focus {
        border-color: #00B8D4;
        color: #00B8D4;
    }
`;

// Validation schema
const validationSchema = yup.object({
    password: yup
        .string()
        .min(6, "At least 6 characters")
        .matches(/[!@#$%^&*(),.?":{}|<>]/, "Password must contain at least one special character")
        .required("Password is required"),
    confirmPassword: yup
        .string()
        .oneOf([yup.ref("password"), null], "Passwords must match")
        .required("Confirm password is required"),
});

const ResetPassword = () => {
    const history = useHistory();
    const passRef = localStorage.getItem("passRef") || "";

    // Define the mutation for resetting the password
    const mutation = useMutation({
        mutationFn: (values) => resetPasswordRequest({ newPassword: values.password, passRef }),
        onSuccess: () => {
            show_toast("Password reset successful.", "success");
            history.push("/login");
        },
        onError: (error) => {
            console.error("Error resetting password", error);
            show_toast(error?.message || "Failed to reset password. Please try again.", "error");
        },
    });

    const handleSubmit = (values) => {
        mutation.mutate(values);
    };

    return (
        <UIOutlet
            logoSrc="/icon.png"
            bannerTitle="Reset Your Password"
            bannerDescription="Enter a new password to reset your account"
        >
            <FormContainer>
                <PageHeader>
                    <Title>Reset Password</Title>
                    <Divider />
                </PageHeader>

                <Formik
                    initialValues={{ password: "", confirmPassword: "" }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ isSubmitting }) => (
                        <Form style={{ width: "100%" }}>
                            <StyledFormItem>
                                <label className="form-label">
                                    New Password <span style={{ color: "#ff4d4f" }}>*</span>
                                </label>
                                <Field name="password">
                                    {({ field }) => (
                                        <Input.Password
                                            {...field}
                                            placeholder="Enter new password"
                                        />
                                    )}
                                </Field>
                                <ErrorMessage
                                    name="password"
                                    component="div"
                                    className="error-message"
                                />
                            </StyledFormItem>

                            <StyledFormItem>
                                <label className="form-label">
                                    Confirm Password <span style={{ color: "#ff4d4f" }}>*</span>
                                </label>
                                <Field name="confirmPassword">
                                    {({ field }) => (
                                        <Input.Password
                                            {...field}
                                            placeholder="Confirm new password"
                                        />
                                    )}
                                </Field>
                                <ErrorMessage
                                    name="confirmPassword"
                                    component="div"
                                    className="error-message"
                                />
                            </StyledFormItem>

                            <ActionButtons>
                                <CancelButton onClick={() => history.push("/verify-code")}>
                                    Back to Verify Code
                                </CancelButton>
                                <SubmitButton
                                    type="primary"
                                    htmlType="submit"
                                    loading={isSubmitting || mutation.isPending}
                                    disabled={isSubmitting || mutation.isPending}
                                >
                                    {isSubmitting || mutation.isPending ? "Processing..." : "Submit"}
                                </SubmitButton>
                            </ActionButtons>
                        </Form>
                    )}
                </Formik>
            </FormContainer>
        </UIOutlet>
    );
};

export default ResetPassword;